import React, { useEffect, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type PendingUpdate = { name: string; sport: string; points: number };

interface TeamsState {
  names: { A: string; B: string };
  members: { A: string[]; B: string[] };
}

interface Props {
  sport: string;
  participants: string[];
  currentSportScores: Record<string, number>; // name -> score for this sport
  onSave: (updates: PendingUpdate[]) => void | Promise<void>;
  isTeamSport?: boolean;
  teams?: TeamsState;
}

const getPointsForRank = (rankIndex: number) => {
  // 1st -> 10, 2nd -> 9, ..., 10th -> 1, beyond -> 0
  return Math.max(0, 10 - rankIndex);
};

const buildInitialOrder = (
  participants: string[],
  currentSportScores: Record<string, number>
) => {
  const clone = [...participants];
  clone.sort((a, b) => {
    const sa = currentSportScores[a] || 0;
    const sb = currentSportScores[b] || 0;
    if (sb !== sa) return sb - sa; // desc by score
    return a.localeCompare(b); // tie-break by name
  });
  return clone;
};

const RowContent: React.FC<{ name: string; index: number; points: number }> = ({
  name,
  index,
  points,
}) => {
  return (
    <>
      <div className="drag-handle left" aria-hidden="true" />
      <div className="rank-position">#{index + 1}</div>
      <div className="rank-name">{name}</div>
      <div className="rank-points">{points} pts</div>
      <div className="drag-handle right" aria-hidden="true" />
    </>
  );
};

const SortableRow: React.FC<{
  id: string;
  index: number;
  points: number;
}> = ({ id, index, points }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rank-item${isDragging ? " dragging" : ""}`}
      {...attributes}
      {...listeners}
    >
      <RowContent name={id} index={index} points={points} />
    </div>
  );
};

const SportSection: React.FC<Props> = ({
  sport,
  participants,
  currentSportScores,
  onSave,
  isTeamSport = false,
  teams,
}) => {
  const [order, setOrder] = useState<string[]>(() =>
    buildInitialOrder(participants, currentSportScores)
  );
  useEffect(() => {
    setOrder(buildInitialOrder(participants, currentSportScores));
  }, [participants, currentSportScores]);

  // Local pending updates for this sport (individual sports only)
  const [pendingUpdates, setPendingUpdates] = useState<PendingUpdate[]>([]);
  useEffect(() => {
    // Clear pending when underlying scores/participants change
    setPendingUpdates([]);
  }, [participants, currentSportScores]);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  const emitTeamWin = (winner: "A" | "B") => {
    if (!teams) return;
    const winners =
      winner === "A" ? teams.members.A || [] : teams.members.B || [];
    const losers =
      winner === "A" ? teams.members.B || [] : teams.members.A || [];
    const wLen = winners.length;
    const lLen = losers.length;
    if (wLen === 0 || lLen === 0) {
      alert("Both teams must have at least one member.");
      return;
    }
    const winnerShare = 50 / wLen;
    const loserShare = 10 / lLen;
    const winnerSet = new Set(winners);
    const loserSet = new Set(losers);
    const updates: PendingUpdate[] = participants.map((name) => {
      let points = 0;
      if (winnerSet.has(name)) points = winnerShare;
      else if (loserSet.has(name)) points = loserShare;
      else points = 0;
      return { name, sport, points };
    });
    onSave(updates);
  };

  const computeAndEmitPending = () => {
    const updates: PendingUpdate[] = order.map((name, idx) => ({
      name,
      sport,
      points: getPointsForRank(idx),
    }));
    setPendingUpdates(updates);
  };

  const activeIndex = activeId ? order.indexOf(activeId) : -1;

  if (isTeamSport && teams) {
    const aMembers = teams.members.A || [];
    const bMembers = teams.members.B || [];
    return (
      <div className="sport-card">
        <div className="sport-card-header">
          <h2>{sport}</h2>
          <div className="sport-actions">
            <button onClick={() => emitTeamWin("A")}>
              {teams.names.A} wins
            </button>
            <button onClick={() => emitTeamWin("B")}>
              {teams.names.B} wins
            </button>
          </div>
        </div>
        <div className="sport-table-scroll">
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <div className="sport-row--head" style={{ padding: 6 }}>
                <div>{teams.names.A}</div>
              </div>
              <div className="rank-list">
                {aMembers.map((name) => (
                  <div
                    key={name}
                    className="rank-item"
                    style={{ gridTemplateColumns: "1fr" }}
                  >
                    <div className="rank-name">{name}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="sport-row--head" style={{ padding: 6 }}>
                <div>{teams.names.B}</div>
              </div>
              <div className="rank-list">
                {bMembers.map((name) => (
                  <div
                    key={name}
                    className="rank-item"
                    style={{ gridTemplateColumns: "1fr" }}
                  >
                    <div className="rank-name">{name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sport-card">
      <div className="sport-card-header">
        <h2>{sport}</h2>
        <div className="sport-actions">
          <button
            onClick={async () => {
              if (pendingUpdates.length === 0) return;
              await onSave(pendingUpdates);
              setPendingUpdates([]);
            }}
            disabled={pendingUpdates.length === 0}
          >
            Save
          </button>
          {pendingUpdates.length > 0 && (
            <span className="pending-count">
              {pendingUpdates.length} pending
            </span>
          )}
        </div>
      </div>

      <div className="sport-table-scroll">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => {
            const id = String(event.active.id);
            setActiveId(id);
          }}
          onDragOver={(event) => {
            const { active, over } = event;
            if (!over) return;
            const activeId = String(active.id);
            const overId = String(over.id);
            if (activeId === overId) return;

            setOrder((prev) => {
              const oldIndex = prev.indexOf(activeId);
              const newIndex = prev.indexOf(overId);
              if (oldIndex === -1 || newIndex === -1) return prev;
              return arrayMove(prev, oldIndex, newIndex);
            });
          }}
          onDragEnd={(event) => {
            setActiveId(null);
            computeAndEmitPending();
          }}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            <div className="rank-list">
              {order.map((name, index) => (
                <SortableRow
                  key={name}
                  id={name}
                  index={index}
                  points={getPointsForRank(index)}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay dropAnimation={null}>
            {activeId ? (
              <div className="drag-overlay">
                <div className="rank-item">
                  <RowContent
                    name={activeId}
                    index={activeIndex}
                    points={getPointsForRank(activeIndex)}
                  />
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default SportSection;
